/**
 * Clipboard Sync Service
 * Handles clipboard synchronization between viewer and remote agent.
 * Compatible with RemoteDesktop.Shared/Models/ClipboardModels.cs
 */

import { SignalRService } from './SignalRService';

/** Matches C# ClipboardData model — uses camelCase for SignalR JSON serialization */
export interface ClipboardData {
    changeId?: string;
    sessionId?: string;
    timestamp?: string; // ISO date string (C# DateTime)
    contentType: number; // 0=None, 1=Text, 2=Image, 3=Files, 4=RichText
    textContent?: string | null;
    htmlContent?: string | null;
    binaryContent?: number[] | null;
    // Legacy fields for backward compat with pre-existing handlers
    format?: string;
    content?: string;
}

export interface ClipboardServiceEvents {
    onRemoteClipboardReceived?: (data: ClipboardData) => void;
    onSyncStatusChanged?: (enabled: boolean) => void;
    onError?: (error: string) => void;
}

export class ClipboardService {
    private signalR: SignalRService;
    private sessionId: string | null = null;
    private events: ClipboardServiceEvents;
    private isSyncEnabled = false;
    private lastSentContent = '';

    constructor(signalR: SignalRService, events: ClipboardServiceEvents = {}) {
        this.signalR = signalR;
        this.events = events;
    }

    setSessionId(sessionId: string | null): void {
        this.sessionId = sessionId;
    }

    /** Enable/disable clipboard sync */
    async setEnabled(enabled: boolean): Promise<void> {
        this.isSyncEnabled = enabled;
        this.events.onSyncStatusChanged?.(enabled);

        if (this.sessionId) {
            try {
                await this.signalR.invoke(
                    'UpdateClipboardSettings', this.sessionId,
                    { enabled, syncText: true, syncImages: false, syncFiles: false, syncRichText: false }
                );
            } catch (error: any) {
                this.events.onError?.(`Clipboard settings error: ${error.message}`);
            }
        }

        if (enabled) {
            this.startListening();
        } else {
            this.stopListening();
        }
    }

    get isEnabled(): boolean {
        return this.isSyncEnabled;
    }

    /** Send local clipboard content to remote agent */
    async sendClipboard(): Promise<void> {
        if (!this.sessionId || !this.isSyncEnabled) return;

        try {
            const text = await navigator.clipboard.readText();
            if (text && text !== this.lastSentContent) {
                this.lastSentContent = text;
                const data: ClipboardData = {
                    sessionId: this.sessionId || '',
                    contentType: 1, // Text
                    textContent: text,
                    timestamp: new Date().toISOString()
                };
                await this.signalR.invoke(
                    'SendClipboardData', this.sessionId, data
                );
            }
        } catch (error: any) {
            // Clipboard API requires user gesture/permission
            if (error.name !== 'NotAllowedError') {
                this.events.onError?.(`Clipboard read error: ${error.message}`);
            }
        }
    }

    /** Handle clipboard data received from remote agent */
    async handleRemoteClipboard(data: ClipboardData): Promise<void> {
        if (!this.isSyncEnabled) return;

        this.events.onRemoteClipboardReceived?.(data);

        // Server sends contentType (enum) + textContent; also handle legacy format/content
        const isText = data.contentType === 1 || data.format === 'text';
        const text = data.textContent || data.content;

        if (isText && text) {
            try {
                await navigator.clipboard.writeText(text);
                this.lastSentContent = text; // Prevent echo
            } catch (error: any) {
                if (error.name !== 'NotAllowedError') {
                    this.events.onError?.(`Clipboard write error: ${error.message}`);
                }
            }
        }
    }

    private copyHandler = (): void => {
        // Small delay to let clipboard content settle
        setTimeout(() => this.sendClipboard(), 100);
    };

    private startListening(): void {
        document.addEventListener('copy', this.copyHandler);
        document.addEventListener('cut', this.copyHandler);
    }

    private stopListening(): void {
        document.removeEventListener('copy', this.copyHandler);
        document.removeEventListener('cut', this.copyHandler);
    }

    dispose(): void {
        this.stopListening();
    }
}
